import { PollCreate } from "@virtualprogaming/js-public-sdk";
import { partial } from "lodash";
import React, { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { useSendMessage, useSendNotificationsToAllChatMembers } from "~/hooks";
import {
  PollResponseData,
  useCreateConversationPoll,
  useUpdateConversationPoll,
} from "~/queries";
import { Button, Icon, LoadingIcon, TextInput } from "~/ui/components";
import { useChatAuthUserData, useToast } from "~/ui/context";
import { timeUntilPollExpires } from "~/utils";

export interface PollFormProps {
  ownerId: number | undefined;
  chatId: number;
  onBack: () => void;
  pollToEdit: PollResponseData | null;
  shouldNotShowAction: boolean;
}

export const PollForm: React.FC<PollFormProps> = ({
  ownerId,
  chatId,
  onBack,
  pollToEdit,
  shouldNotShowAction = false,
}) => {
  const toast = useToast();
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PollCreate>({
    defaultValues: { title: "", active: true, expires_in_days: 1 },
  });

  const { chatAuthUser } = useChatAuthUserData();

  const {
    mutateAsync: createPoll,
    isLoading: isCreating,
  } = useCreateConversationPoll();
  const {
    mutateAsync: updatePoll,
    isLoading: isUpdating,
  } = useUpdateConversationPoll();

  const handleClose = useCallback(() => {
    reset();
  }, [reset]);

  const { sendMessage } = useSendMessage(chatId);

  useEffect(() => {
    if (pollToEdit) {
      const data = {
        title: pollToEdit.poll.title,
        expires_in_days: pollToEdit.poll.expires_in_days,
      };
      reset(data);
    }
  }, [pollToEdit, reset]);

  const { sendNotificationToAllMembers } = useSendNotificationsToAllChatMembers(
    chatId
  );

  const onFinish = useCallback(
    (message: string) => {
      handleClose();
      sendMessage(message, partial(sendNotificationToAllMembers, message));
      onBack();
    },
    [handleClose, onBack, sendNotificationToAllMembers, sendMessage]
  );

  const onSubmit = useCallback(
    (params: PollCreate) => {
      if (
        shouldNotShowAction &&
        (!ownerId || !chatAuthUser || !chatAuthUser?.user.uid)
      )
        return;
      const id = shouldNotShowAction
        ? 3
        : Number(chatAuthUser?.user?.uid?.substring(1));
      if (chatAuthUser?.user?.uid?.substring(1) !== `${id}`) return;
      if (pollToEdit) {
        updatePoll([chatId, id, params]).then((res) => {
          toast({
            message: intl.formatMessage({
              defaultMessage: "You have successfully updated the poll.",
              id: "Jd0Jw+",
            }),
          });
          const message = `Poll: ${pollToEdit.poll.title} was updated to ${
            params.title
          }. You have ${timeUntilPollExpires(
            res.data.created_at,
            res.data.expires_in_days
          )} vote to on this poll.`;
          onFinish(message);
        });
      } else {
        createPoll([chatId, id, { ...params, active: true }]).then((res) => {
          toast({
            message: intl.formatMessage({
              defaultMessage: "You have successfully created a new poll.",
              id: "enxB1W",
            }),
          });
          const message = `New poll was created: ${
            params.title
          }. You have ${timeUntilPollExpires(
            res.data.created_at,
            res.data.expires_in_days
          )} vote to on this poll.`;
          onFinish(message);
        });
      }
    },
    [
      shouldNotShowAction,
      ownerId,
      chatAuthUser,
      pollToEdit,
      updatePoll,
      chatId,
      toast,
      intl,
      onFinish,
      createPoll,
    ]
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-md pd-md mg-t-md"
    >
      {pollToEdit && (
        <span className="text-muted flex flex-row items-center gap-sm">
          <Icon icon={["fas", "info-square"]} size="1x" />
          <FormattedMessage
            defaultMessage="Creating a new poll will delete and replace the previous one."
            id="mFCGQe"
          />
        </span>
      )}
      <TextInput
        label={intl.formatMessage({
          defaultMessage: "Title",
          id: "9a9+ww",
        })}
        boxSize="sm"
        {...register("title", { required: true, maxLength: 70 })}
        error={errors.title}
      />
      <TextInput
        label={intl.formatMessage({
          defaultMessage: "Expires in days (optional)",
          id: "hnjDAP",
        })}
        boxSize="sm"
        type="number"
        {...register("expires_in_days", {
          required: false,
          max: 10,
          min: 1,
        })}
        error={errors.expires_in_days}
      />
      <span className="text-muted flex flex-row items-center gap-sm">
        <Icon icon={["fas", "info-square"]} size="1x" />
        <FormattedMessage
          defaultMessage="Default expiration time of polls is 1 day."
          id="dYTTUV"
        />
      </span>

      <div className="flex flex-row items-center gap-xxl mg-t-md">
        <Button
          type="button"
          variant="text"
          size="sm"
          onClick={onBack}
          className="font-variant-small-caps flex items-center justify-center gap-md "
        >
          <Icon icon={["far", "angle-left"]} size="lg" />
        </Button>

        <Button
          type="submit"
          variant="success"
          size="sm"
          className="font-variant-small-caps flex items-center justify-between gap-md w-100"
        >
          <FormattedMessage defaultMessage="Save" id="jvo0vs" />
          {isCreating || isUpdating ? (
            <LoadingIcon size="lg" />
          ) : (
            <Icon icon={["fal", "pen"]} size="lg" />
          )}
        </Button>
      </div>
    </form>
  );
};
